import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, map, mergeMap, skipWhile, tap, withLatestFrom } from 'rxjs/operators';
import { CommentEntityNameEnum, ThreadViewModel } from '../../models';
import { CommentingFacadeService, CommentPanelManagerService, FocusingService } from '../../services';
import { createComposedResourceId } from '../../utils';
import {
  focusedReplyIdQueryParam,
  focusedThreadIdQueryParam,
  selectedSingleThreadControlNameQueryParam,
} from '../../constants';

@Injectable()
export class FocusingEffects {
  constructor(
    private router: Router,
    private commentPanelManager: CommentPanelManagerService,
    private commentFacade: CommentingFacadeService,
    private focusingService: FocusingService
  ) { }

  handleThreadQueryParam$ = createEffect(() => this.getQueryParamStream(focusedThreadIdQueryParam).pipe(
    mergeMap(async (focusedReplyId) => {
      return { focusedReplyId, resources: await this.getCommentingResources() };
    }),
    map((aggregated) => aggregated.resources.find((thread) => thread.threadId === aggregated.focusedReplyId)),
    skipWhile((focusedResource) => !focusedResource),
    tap((focusedResource) => this.handleFocusedResource(focusedResource)),
    tap((focusedResource) =>
      this.focusingService.focusResources({
        [CommentEntityNameEnum.Thread]: focusedResource.threadId,
        [CommentEntityNameEnum.Resource]: createComposedResourceId(
          focusedResource.resourceType,
          focusedResource.resourceId
        ),
      })
    ),
    tap(() => this.clearQueryParam(focusedThreadIdQueryParam))
  ), { dispatch: false });

  handleReplyQueryParam$ = createEffect(() => this.getQueryParamStream(focusedReplyIdQueryParam).pipe(
    mergeMap(async (focusedReplyId) => {
      return { focusedReplyId, resources: await this.getCommentingResources() };
    }),
    withLatestFrom(this.commentFacade.getAllComments()),
    map(([aggregated, comments]) => {
      const focusedComment = comments.find((comment) => comment.id === aggregated.focusedReplyId);
      return {
        resources: aggregated.resources,
        focusedReplyId: focusedComment.id,
        focusedThreadId: focusedComment.threadId,
      };
    }),
    map((aggregated) => ({
      focusedReplyId: aggregated.focusedReplyId,
      focusedResource: aggregated.resources.find((thread) => thread.threadId === aggregated.focusedThreadId),
    })),
    skipWhile((aggregated) => !aggregated.focusedResource),
    tap((aggregated) => this.handleFocusedResource(aggregated.focusedResource)),
    tap((aggregated) => {
      this.focusingService.focusResources({
        [CommentEntityNameEnum.Thread]: aggregated.focusedResource.threadId,
        [CommentEntityNameEnum.Resource]: createComposedResourceId(
          aggregated.focusedResource.resourceType,
          aggregated.focusedResource.resourceId
        ),
        [CommentEntityNameEnum.Comment]: aggregated.focusedReplyId,
      });
    }),
    tap((aggregated) => {
      const extraQueryParam = {
        [selectedSingleThreadControlNameQueryParam]: aggregated.focusedResource.resourceId,
      };
      this.clearQueryParam(focusedReplyIdQueryParam, extraQueryParam);
    })
  ), { dispatch: false });

  private getQueryParamStream(queryParamName: string): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.routerState.snapshot.root.queryParams[queryParamName]),
      filter((queryParamValue) => !!queryParamValue)
    );
  }

  private getCommentingResources(): Promise<ThreadViewModel[]> {
    return firstValueFrom(
      this.commentPanelManager.getCommentingResources()
        .pipe(skipWhile((resources) => !resources.length))
    );
  }

  private clearQueryParam(
    queryParamName: string,
    extraQueryParam?: {
      [key: string]: string;
    }
  ): void {
    const queryParams = { ...this.router.routerState.snapshot.root.queryParams, ...extraQueryParam };
    delete queryParams[queryParamName];
    this.router.navigate([], { queryParams, replaceUrl: true });
  }

  private handleFocusedResource(threadViewModel: ThreadViewModel): void {
    this.commentPanelManager.focusComment(threadViewModel.resourceType, threadViewModel.resourceId);
    this.commentPanelManager.displaySingleThread(threadViewModel.resourceType, threadViewModel.resourceId);
  }
}
